/**
 * Operation + agent metadata for the Workflow Versions v2 surface.
 *
 * Ported verbatim from the mockup's
 * `features/projects/components/VersionsPanel/__demos__/mockData.js`
 * (definitions only — no mock records). These tables describe the
 * shape of the live data; they don't add fields that the backend
 * doesn't ship. The per-agent column registry mirrors each Step's
 * live DataTable so commit-list diffs read like a row peeled from
 * the same table.
 */
import { OperationType } from '../state/types';

export type OperationTone =
  | 'info'
  | 'neutral'
  | 'success'
  | 'warning'
  | 'error';

export interface OperationMeta {
  label: string;
  short: string;
  tone: OperationTone;
  glyph:
    | 'flag'
    | 'bookmark'
    | 'plus'
    | 'upload'
    | 'pencil'
    | 'minus'
    | 'rewind'
    | 'refresh';
  description: string;
}

export const OPERATION_META: Record<OperationType, OperationMeta> = {
  baseline: {
    label: 'Baseline',
    short: 'BASE',
    tone: 'info',
    glyph: 'flag',
    description: 'Captured on first workflow completion.',
  },
  snapshot: {
    label: 'Snapshot',
    short: 'SNAP',
    tone: 'neutral',
    glyph: 'bookmark',
    description: 'Periodic state snapshot used to bound restore cost.',
  },
  add: {
    label: 'Added',
    short: 'ADD',
    tone: 'success',
    glyph: 'plus',
    description: 'New record inserted.',
  },
  update: {
    label: 'Edited',
    short: 'EDIT',
    tone: 'info',
    glyph: 'pencil',
    description: 'Field values changed.',
  },
  delete: {
    label: 'Deleted',
    short: 'DEL',
    tone: 'error',
    glyph: 'minus',
    description: 'Record removed.',
  },
  restore: {
    label: 'Restored',
    short: 'RSTR',
    tone: 'warning',
    glyph: 'rewind',
    description: 'Reverted to a previous version.',
  },
  revise_recompletion: {
    label: 'Revised',
    short: 'REV',
    tone: 'warning',
    glyph: 'refresh',
    description: 'Agent rerun re-computed downstream results.',
  },
};

// ────────────────────────────────────────────────────────────────
// Agent display-name registry — maps `agent_name` strings from the
// backend into the human labels used in the UI's TARA workflow.
// ────────────────────────────────────────────────────────────────
export const AGENT_DISPLAY_NAMES: Record<string, string> = {
  analysis_agent: 'Item Definition',
  asset_identification_agent: 'Assets',
  damage_scenario_agent: 'Damage Scenarios',
  threat_analysis_agent: 'Threats',
  attack_path_feasibility_agent: 'Attack Paths',
  risk_assessment_agent: 'Risk Assessments',
  cybersecurity_goals_agent: 'Cybersecurity Goals',
  cybersecurity_concept_agent: 'Cybersecurity Concept',
};

// ────────────────────────────────────────────────────────────────
// Per-agent column registry. Mirrors the columns of each Step's
// live DataTable. `type` drives cell rendering in MiniRow /
// FieldDiff (introduced in later commits).
// ────────────────────────────────────────────────────────────────
export type AgentColumnType =
  | 'id'
  | 'ref'
  | 'refs'
  | 'chips'
  | 'text'
  | 'longText'
  | 'numeric'
  | 'levelBadge'
  | 'categoryPill';

export interface AgentColumn {
  key: string;
  label: string;
  flex: string;
  type: AgentColumnType;
}

export const AGENT_COLUMNS: Record<string, AgentColumn[]> = {
  asset_identification_agent: [
    { key: 'asset_id', label: 'ID', flex: '0 0 90px', type: 'id' },
    { key: 'asset_name', label: 'Name', flex: '0 0 180px', type: 'text' },
    { key: 'asset_type', label: 'Type', flex: '0 0 120px', type: 'text' },
    { key: 'asset_description', label: 'Description', flex: '1 1 240px', type: 'longText' },
    { key: 'security_properties', label: 'Security', flex: '0 0 170px', type: 'chips' },
  ],
  damage_scenario_agent: [
    { key: 'damage_id', label: 'ID', flex: '0 0 80px', type: 'id' },
    { key: 'asset_ids', label: 'Assets', flex: '0 0 110px', type: 'refs' },
    { key: 'damage_scenario', label: 'Scenario', flex: '1 1 240px', type: 'longText' },
    { key: 'safety', label: 'S', flex: '0 0 78px', type: 'levelBadge' },
    { key: 'financial', label: 'F', flex: '0 0 78px', type: 'levelBadge' },
    { key: 'operational', label: 'O', flex: '0 0 78px', type: 'levelBadge' },
    { key: 'privacy', label: 'P', flex: '0 0 78px', type: 'levelBadge' },
  ],
  threat_analysis_agent: [
    { key: 'threat_id', label: 'ID', flex: '0 0 90px', type: 'id' },
    { key: 'stride_category', label: 'STRIDE', flex: '0 0 140px', type: 'categoryPill' },
    { key: 'target_asset', label: 'Asset', flex: '0 0 100px', type: 'ref' },
    { key: 'damage_ids', label: 'Damage', flex: '0 0 110px', type: 'refs' },
    { key: 'threat_scenario', label: 'Scenario', flex: '1 1 260px', type: 'longText' },
  ],
  attack_path_feasibility_agent: [
    { key: 'attack_path_id', label: 'ID', flex: '0 0 90px', type: 'id' },
    { key: 'threat_id', label: 'Threat', flex: '0 0 90px', type: 'ref' },
    { key: 'asset_id', label: 'Asset', flex: '0 0 90px', type: 'ref' },
    { key: 'elapsed_time', label: 'Time', flex: '0 0 56px', type: 'numeric' },
    { key: 'specialist_expertise', label: 'Exp', flex: '0 0 56px', type: 'numeric' },
    { key: 'knowledge_of_item', label: 'Know', flex: '0 0 56px', type: 'numeric' },
    { key: 'window_of_opportunity', label: 'Wnd', flex: '0 0 56px', type: 'numeric' },
    { key: 'equipment', label: 'Equip', flex: '0 0 60px', type: 'numeric' },
    { key: 'total_attack_potential', label: 'Total', flex: '0 0 60px', type: 'numeric' },
    { key: 'attack_feasibility_rating', label: 'Feasibility', flex: '0 0 110px', type: 'levelBadge' },
  ],
  risk_assessment_agent: [
    { key: 'risk_id', label: 'ID', flex: '0 0 90px', type: 'id' },
    { key: 'asset_id', label: 'Asset', flex: '0 0 90px', type: 'ref' },
    { key: 'threat_id', label: 'Threat', flex: '0 0 90px', type: 'ref' },
    { key: 'damage_id', label: 'Damage', flex: '0 0 90px', type: 'ref' },
    { key: 'risk_value', label: 'Risk', flex: '0 0 70px', type: 'levelBadge' },
    { key: 'treatment_decision', label: 'Treatment', flex: '0 0 120px', type: 'levelBadge' },
    { key: 'treatment_priority', label: 'Priority', flex: '0 0 110px', type: 'levelBadge' },
    { key: 'rationale', label: 'Justification', flex: '1 1 220px', type: 'longText' },
  ],
  cybersecurity_goals_agent: [
    { key: 'goal_id', label: 'ID', flex: '0 0 90px', type: 'id' },
    { key: 'goal_statement', label: 'Statement', flex: '1 1 280px', type: 'longText' },
    { key: 'related_risk_ids', label: 'Risks', flex: '0 0 130px', type: 'refs' },
    { key: 'security_property', label: 'Property', flex: '0 0 140px', type: 'categoryPill' },
  ],
  cybersecurity_concept_agent: [
    { key: 'control_id', label: 'ID', flex: '0 0 90px', type: 'id' },
    { key: 'control_name', label: 'Name', flex: '0 0 180px', type: 'text' },
    { key: 'control_description', label: 'Description', flex: '1 1 240px', type: 'longText' },
    { key: 'control_type', label: 'Type', flex: '0 0 140px', type: 'categoryPill' },
    { key: 'goal_id', label: 'Goal', flex: '0 0 100px', type: 'ref' },
  ],
};

/** Looks up the friendly label for an agent_name string, falling
 *  back to a humanised version of the raw name when not in the
 *  registry. */
export function agentDisplayName(agentName: string | null | undefined): string {
  if (!agentName) return '—';
  return AGENT_DISPLAY_NAMES[agentName] || agentName;
}
